import { CeoPulse } from "@/components/v3/CeoPulse";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { formatCr, formatNumber } from "@/lib/format";
import { ArrowUpRight, ArrowDownRight, Minus, Info, PhoneCall, AlertTriangle, Printer } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
  ComposedChart, Bar, Legend,
} from "recharts";
import CockpitLive from "@/components/v3/CockpitLive";
import { DataFreshness } from "@/components/v3/DataFreshness";
import { useLiveRefresh } from "@/hooks/useLiveRefresh";
import LiveCockpit from "@/pages/v3/LiveCockpit";
import { ReportViewSelector } from "@/components/v3/ReportViewSelector";
import { SourceCoverage } from "@/components/v3/SourceCoverage";

const GOOD = "#059669", WARN = "#EA580C", CRIT = "#DC2626", BRAND = "#b3122a", INK = "#111111";

// Severity ramp — agency palette (slate → yellow → orange → brand red)
const BUCKET_COLOR = {
  "On time": "#059669",
  "0-30 DPD": "#cbd5e1",
  "31-60 DPD": "#eab308",
  "61-90 DPD": "#EA580C",
  "91-365 DPD": "#b3122a",
};
const BUCKET_ORDER = ["On time", "0-30 DPD", "31-60 DPD", "61-90 DPD", "91-365 DPD"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const pct1 = (v) => `${(Number(v) || 0).toFixed(1)}%`;
const round0 = (v) => formatNumber(Math.round(Number(v) || 0));
const mLabel = (m) => MONTHS[Number(m.slice(5, 7)) - 1] || m;

function Delta({ value, unit, goodWhenUp = true }) {
  if (value == null || !isFinite(value))
    return <span className="text-[11px] text-slate-400">no prior data</span>;
  const up = value > 0, flat = Math.abs(value) < 0.05;
  const good = flat ? null : (up === goodWhenUp);
  const cls = good == null ? "bg-slate-100 text-slate-500"
    : good ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700";
  const Icon = flat ? Minus : up ? ArrowUpRight : ArrowDownRight;
  const txt = unit === "pp" ? `${Math.abs(value).toFixed(1)} pp` : `${Math.abs(value).toFixed(1)}%`;
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[11px] font-semibold ${cls}`} data-testid="kpi-delta">
      <Icon size={11} strokeWidth={2.5} /> {txt}
      <span className="font-normal opacity-60 ml-0.5">MoM</span>
    </span>
  );
}

function StatTile({ label, value, delta, onClick, testid, hero }) {
  return (
    <div onClick={onClick} data-testid={testid}
      className={`ceo-card p-5 ${hero ? "border-t-2 border-t-[#b3122a]" : ""} ${onClick ? "cursor-pointer ceo-card--click" : ""}`}>
      <div className="ceo-label">{label}</div>
      <div className="ceo-fig text-[28px] leading-tight text-[#111111] mt-2 tabular-nums">{value}</div>
      <div className="mt-2">{delta}</div>
    </div>
  );
}

function Card({ title, note, children, className = "", testid, right }) {
  return (
    <div className={`ceo-card p-6 ${className}`} data-testid={testid}>
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <h3 className="text-[15px] font-bold text-[#111111] tracking-tight">{title}</h3>
          {note && <p className="text-xs text-slate-400 mt-1">{note}</p>}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

function MiniStat({ label, value, sub, color }) {
  return (
    <div>
      <div className="ceo-label">{label}</div>
      <div className="ceo-fig text-[19px] text-[#111111] mt-1 tabular-nums" style={color ? { color } : undefined}>{value}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

const tooltipStyle = { borderRadius: 6, border: "1px solid #e2e8f0", fontSize: 12, boxShadow: "0 4px 12px -4px rgba(15,23,42,0.12)" };

export default function Cockpit() {
  const [params] = useSearchParams();
  return params.get("view") === "history" ? <HistoricalCockpit /> : <LiveCockpit />;
}

function HistoricalCockpit() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [dates, setDates] = useState([]);
  const [asOf, setAsOf] = useState("");
  const [cur, setCur] = useState(null);
  const [prior, setPrior] = useState(null);
  const [charts, setCharts] = useState(null);
  const [fac, setFac] = useState(null);
  const [trend, setTrend] = useState([]);
  const [pipe, setPipe] = useState(null);
  const [approved, setApproved] = useState(null);
  const [dsa, setDsa] = useState(null);
  const [cash, setCash] = useState(null);
  const [cashYear, setCashYear] = useState(null);
  const [overdue, setOverdue] = useState(null);
  const [error, setError] = useState("");
  const [cap, setCap] = useState(() => localStorage.getItem("rf_concentration_cap") || "");
  const [refreshKey, refresh] = useLiveRefresh();
  const selectedDate = params.get("as_of");
  useEffect(() => { if (selectedDate && dates.includes(selectedDate)) setAsOf(selectedDate); }, [selectedDate, dates]);

  useEffect(() => {
    api.get("/portfolio/as-of-dates").then((r) => { setDates(r.data.dates || []); setAsOf(current => current || r.data.latest || ""); if (!r.data.latest) setError("Historical snapshots are unavailable."); }).catch(() => setError("Historical snapshot dates could not be loaded."));
    api.get("/portfolio/trends", { params: { months: 60 } }).then((r) => setTrend(r.data.trend || [])).catch(() => {});
    api.get("/pipeline/summary").then((r) => setPipe(r.data)).catch(() => {});
    api.get("/approved/summary").then((r) => setApproved(r.data)).catch(() => {});
    api.get("/dsa/summary").then((r) => setDsa(r.data)).catch(() => {});
  }, [refreshKey]);

  useEffect(() => {
    api.get("/portfolio/cashflow", { params: cashYear ? { year: cashYear } : {} })
      .then((r) => { setCash(r.data); if (!cashYear) setCashYear(r.data.year); })
      .catch(() => {});
  }, [cashYear, refreshKey]);

  useEffect(() => {
    if (!asOf) return;
    const idx = dates.indexOf(asOf);
    const prevDate = idx >= 0 && idx + 1 < dates.length ? dates[idx + 1] : null;
    Promise.all([
      api.get("/portfolio/summary", { params: { as_of: asOf } }),
      api.get("/portfolio/charts", { params: { as_of: asOf } }),
      api.get("/portfolio/facilities", { params: { as_of: asOf } }),
      api.get("/portfolio/overdue-accounts", { params: { as_of: asOf, limit: 8 } }),
      prevDate ? api.get("/portfolio/summary", { params: { as_of: prevDate } }) : Promise.resolve({ data: null }),
    ]).then(([s, c, f, o, p]) => { setCur(s.data); setCharts(c.data); setFac(f.data); setOverdue(o.data.accounts); setPrior(p.data); })
      .catch((e) => setError(e.response?.data?.detail || e.message));
  }, [asOf, dates, refreshKey]);

  const selMonth = asOf ? asOf.slice(0, 7) : "";
  const selIdx = trend.findIndex((t) => t.month === selMonth);
  const selRow = selIdx >= 0 ? trend[selIdx] : null;
  const prevRow = selIdx > 0 ? trend[selIdx - 1] : null;
  const aumTrend = trend.slice(-12);

  // Cash-flow YTD aggregates
  const ytd = useMemo(() => {
    const ms = cash?.months || [];
    const sum = (k) => ms.reduce((a, b) => a + (b[k] || 0), 0);
    const invoiced = sum("invoiced"), collected = sum("collected");
    return {
      invoiced, collected,
      lateFeesDue: sum("other_due"), lateFeesCollected: sum("other_collected"),
      principal: sum("principal_collected"), interest: sum("interest_collected"),
      disbursed: sum("disbursed"),
      efficiency: invoiced ? (collected / invoiced) * 100 : 0,
    };
  }, [cash]);
  const cashData = useMemo(
    () => (cash?.months || []).map((m) => ({ ...m, label: mLabel(m.month) })),
    [cash]
  );

  if (error) return <div className="p-8 text-[#DC2626]" data-testid="cockpit-error">Failed to load: {error}</div>;
  if (!cur || !charts || !fac) return <div className="p-8 text-slate-400" data-testid="cockpit-loading">Loading cockpit…</div>;

  const pctChange = (a, b) => (b ? ((a - b) / b) * 100 : null);
  const pp = (a, b) => (b == null || a == null ? null : a - b);
  const goLoans = (q = {}) => navigate({ pathname: "/portfolio", search: new URLSearchParams(q).toString() });

  // DPD aging (real buckets only)
  const dpd = BUCKET_ORDER.map((name) => charts.dpd_buckets.find((d) => d.name === name)).filter(Boolean);
  const dpdTotal = dpd.reduce((a, b) => a + b.outstanding, 0) || 1;
  const osOf = (name) => dpd.find((d) => d.name === name)?.outstanding || 0;
  const par30 = osOf("31-60 DPD") + osOf("61-90 DPD") + osOf("91-365 DPD");
  const par90 = osOf("91-365 DPD");

  // AUM mix — Term Loans vs Invoice Discounting (from real product field)
  const byProduct = charts.by_product || [];
  const idRow = byProduct.find((p) => p.name === "Invoice Discounting");
  const idVal = idRow?.value || 0;
  const termVal = byProduct.filter((p) => p.name !== "Invoice Discounting").reduce((a, b) => a + b.value, 0);
  const mixTotal = idVal + termVal || 1;

  // Concentration
  const activeAum = fac.aum || 1;
  const top = charts.top_borrowers || [];
  const top10pct = (top.reduce((a, b) => a + b.value, 0) / activeAum) * 100;
  const largestPct = charts.largest_single_pct;
  const capNum = cap === "" ? null : Number(cap);
  const flagged = capNum != null ? top.filter((b) => (b.value / activeAum) * 100 > capNum) : [];
  const onCap = (v) => { setCap(v); if (v === "") localStorage.removeItem("rf_concentration_cap"); else localStorage.setItem("rf_concentration_cap", v); };

  // Funnel
  const received = pipe?.total_applications ?? 0;
  const inReview = pipe?.active_pipeline ?? 0;
  const appr = pipe?.approved ?? 0;
  const funnel = [
    { label: "Applications received", count: received },
    { label: "In review / field visit", count: inReview },
    { label: "Approved", count: appr },
  ];
  const fnMax = received || 1;
  const latest = dates[0];
  const isLatest = asOf === latest;
  const staleDays = latest ? Math.floor((Date.now() - new Date(latest).getTime()) / 86400000) : 0;
  const staleMonths = Math.floor(staleDays / 30);

  // Collections mix segments
  const colTotal = ytd.collected || 1;
  const mix = [
    { label: "Principal", v: ytd.principal, color: INK },
    { label: "Interest", v: ytd.interest, color: BRAND },
    { label: "Late fees / charges", v: ytd.lateFeesCollected, color: WARN },
  ];
  const netFlow = ytd.collected - ytd.disbursed;

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1560px] financial-report" data-testid="cockpit-page">
      <header className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="ceo-label !text-[#b3122a]">Red Fort Capital Loan Pulse · Historical portfolio</div>
          <h1 className="ceo-fig text-4xl text-[#111111] mt-1.5">CEO Cockpit</h1>
          <p className="text-slate-500 mt-2 text-[13px]">
            Historical loan book: {round0(cur.active_facilities)} active facilities · {round0(cur.total_facilities)} total · snapshot {asOf}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2.5">
          <ReportViewSelector dates={dates} value={asOf} />
          <button data-testid="ceo-print-report" className="rf-btn-primary no-print" onClick={() => window.print()}><Printer size={15} /> Print report</button>
        </div>
      </header>

      <div className="report-print-heading">CONFIDENTIAL · RED FORT CAPITAL LOAN PULSE · Historical portfolio {asOf} · HQ operations · Printed {new Date().toLocaleDateString()}</div>

      <DataFreshness version={refreshKey} onRefresh={refresh} prefix="cockpit" />

      <SourceCoverage version={refreshKey} />
      <CeoPulse historical />

      {staleDays > 40 && (
        <div className="mb-8 flex items-start gap-3 border border-amber-300 bg-amber-50 rounded-sm px-4 py-3" data-testid="stale-data-banner">
          <AlertTriangle size={17} strokeWidth={1.5} className="text-amber-600 mt-0.5 shrink-0" />
          <div>
            <div className="text-[13px] font-bold text-amber-800">
              Loan book data is {staleMonths >= 2 ? `${staleMonths} months` : `${staleDays} days`} old — last snapshot {latest}
            </div>
            <div className="text-[12px] text-amber-700 mt-0.5">
              No newer <span className="font-semibold">loanbooks</span> snapshot is available in the connected LMS database. AUM, DPD and portfolio cash flow reflect that month-end. Live applications use a separate source and are not restricted by this date.
            </div>
          </div>
        </div>
      )}

      <CockpitLive refreshKey={refreshKey} />

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8" data-testid="kpi-strip">
        <StatTile hero testid="kpi-aum" label="AUM (outstanding)" value={formatCr(cur.aum)} onClick={() => goLoans()}
          delta={<Delta value={pctChange(cur.aum, prior?.aum)} unit="pct" goodWhenUp />} />
        <StatTile testid="kpi-disbursements" label="Disbursements (mo)"
          value={selRow ? formatCr(selRow.disbursements) : "—"}
          delta={<Delta value={selRow && prevRow ? pctChange(selRow.disbursements, prevRow.disbursements) : null} unit="pct" goodWhenUp />} />
        <StatTile testid="kpi-collection" label="Collection efficiency"
          value={selRow ? pct1(selRow.collection_efficiency) : "—"}
          delta={<Delta value={selRow && prevRow ? pp(selRow.collection_efficiency, prevRow.collection_efficiency) : null} unit="pp" goodWhenUp />} />
        <StatTile testid="kpi-yield" label="Weighted-avg yield" value={pct1(cur.weighted_yield)}
          delta={<Delta value={pp(cur.weighted_yield, prior?.weighted_yield)} unit="pp" goodWhenUp />} />
        <StatTile testid="kpi-par" label="PAR %" value={pct1(cur.par_pct)} onClick={() => goLoans()}
          delta={<Delta value={pp(cur.par_pct, prior?.par_pct)} unit="pp" goodWhenUp={false} />} />
        <StatTile testid="kpi-gnpa" label="GNPA %" value={pct1(cur.gnpa_pct)} onClick={() => goLoans({ bucket: "91-365 DPD" })}
          delta={<Delta value={pp(cur.gnpa_pct, prior?.gnpa_pct)} unit="pp" goodWhenUp={false} />} />
      </div>

      {/* Cash flow + Net flow / Collections mix */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
        <Card title="Cash flow — EMI billed vs invoiced vs collected"
          note="Monthly · late fees & penal charges (DPD) shown as a separate invoiced layer"
          className="lg:col-span-8" testid="card-cashflow"
          right={cash && (
            <div className="flex items-center gap-2">
              <span className="ceo-label">Year</span>
              <select value={cashYear || ""} onChange={(e) => setCashYear(e.target.value)} data-testid="cashflow-year"
                className="px-2 py-1.5 rounded-none border-0 border-b-2 border-slate-900 bg-transparent text-sm font-semibold text-slate-800 focus:border-[#b3122a] outline-none cursor-pointer">
                {(cash.years || []).map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          )}>
          {cashData.length ? (
            <>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={cashData} margin={{ left: 0, right: 8, top: 8 }} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={(v) => formatCr(v)} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={66} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v, name) => [formatCr(v), name]}
                    labelFormatter={(l, p) => p?.[0]?.payload?.month || l} />
                  <Legend wrapperStyle={{ fontSize: 12 }} iconType="square" iconSize={9} />
                  <Bar dataKey="emi" name="EMI billed" fill="#cbd5e1" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="pi_due" name="Invoiced (P+I)" stackId="inv" fill="#334155" />
                  <Bar dataKey="other_due" name="Late fees invoiced" stackId="inv" fill={WARN} radius={[2, 2, 0, 0]} />
                  <Line dataKey="collected" name="Collected (₹)" stroke={BRAND} strokeWidth={2.5}
                    dot={{ r: 3, fill: BRAND, strokeWidth: 0 }} activeDot={{ r: 5 }} />
                </ComposedChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5 pt-5 border-t border-slate-100" data-testid="cashflow-ytd">
                <MiniStat label={`Invoiced ${cash.year}`} value={formatCr(ytd.invoiced)} sub="incl. late fees" />
                <MiniStat label={`Collected ${cash.year}`} value={formatCr(ytd.collected)} sub={`${pct1(ytd.efficiency)} of invoiced`} color={GOOD} />
                <MiniStat label="Shortfall" value={formatCr(Math.max(ytd.invoiced - ytd.collected, 0))} sub="invoiced − collected" color={CRIT} />
                <MiniStat label="Late fees collected" value={formatCr(ytd.lateFeesCollected)} sub={`of ${formatCr(ytd.lateFeesDue)} charged`} color={WARN} />
              </div>
            </>
          ) : <div className="text-slate-400 text-sm py-16 text-center">Not available</div>}
        </Card>

        <div className="lg:col-span-4 flex flex-col gap-6">
          <Card title={`Net flow · ${cash?.year || ""}`} note="Money in (collections) vs money out (disbursements)" testid="card-netflow">
            <div className="space-y-4">
              {[
                { l: "Collected", v: ytd.collected, color: GOOD },
                { l: "Disbursed", v: ytd.disbursed, color: INK },
              ].map((r) => (
                <div key={r.l}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[13px] text-slate-600">{r.l}</span>
                    <span className="ceo-fig text-[15px] text-[#111111] tabular-nums">{formatCr(r.v)}</span>
                  </div>
                  <div className="h-2 bg-slate-100 overflow-hidden rounded-sm">
                    <div className="h-full rounded-sm" style={{ width: `${(r.v / (Math.max(ytd.collected, ytd.disbursed) || 1)) * 100}%`, background: r.color }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
              <span className="ceo-label">Net {netFlow >= 0 ? "recovery" : "deployment"}</span>
              <span className="ceo-fig text-[18px] tabular-nums" style={{ color: netFlow >= 0 ? GOOD : BRAND }} data-testid="netflow-value">
                {netFlow >= 0 ? "+" : "−"}{formatCr(Math.abs(netFlow))}
              </span>
            </div>
          </Card>

          <Card title={`Collections mix · ${cash?.year || ""}`} note="Split of every rupee collected" testid="card-collections-mix">
            <div className="flex w-full h-3 rounded-sm overflow-hidden mb-4">
              {mix.map((m) => (
                <div key={m.label} style={{ width: `${(m.v / colTotal) * 100}%`, background: m.color }} title={`${m.label}: ${formatCr(m.v)}`} />
              ))}
            </div>
            <div className="space-y-2.5">
              {mix.map((m) => (
                <div key={m.label} className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-[13px] text-slate-600">
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ background: m.color }} />{m.label}
                  </span>
                  <span className="text-[13px] font-semibold text-[#111111] tabular-nums">
                    {formatCr(m.v)} <span className="text-slate-400 font-normal">· {((m.v / colTotal) * 100).toFixed(1)}%</span>
                  </span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {/* Accounts to chase + AUM mix */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
        <Card title="Accounts to chase — top overdue" note={`Active book · P + I + charges overdue · as of ${asOf}`}
          className="lg:col-span-7" testid="card-overdue"
          right={<PhoneCall size={16} strokeWidth={1.5} className="text-slate-300" />}>
          {overdue === null ? (
            <div className="text-slate-400 text-sm">Loading…</div>
          ) : overdue.length ? (
            <div className="divide-y divide-slate-100 -mx-2" data-testid="overdue-list">
              {overdue.map((a, i) => (
                <button key={a.id} onClick={() => goLoans({ borrower: a.borrower })} data-testid={`overdue-account-${i}`}
                  className="ceo-row w-full flex items-center gap-3 text-left px-2 py-2.5">
                  <span className="ceo-fig text-[13px] text-slate-300 w-5 shrink-0">{i + 1}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[13px] font-semibold text-[#111111] truncate">{a.borrower}</span>
                    <span className="block text-[11px] text-slate-400 truncate">{a.facility} · {a.product}</span>
                  </span>
                  <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded shrink-0"
                    style={{ background: `${BUCKET_COLOR[a.bucket] || "#64748b"}18`, color: BUCKET_COLOR[a.bucket] === "#cbd5e1" ? "#64748b" : (BUCKET_COLOR[a.bucket] || "#64748b") }}>
                    {a.bucket}
                  </span>
                  <span className="text-right shrink-0 w-[110px]">
                    <span className="block ceo-fig text-[15px] tabular-nums" style={{ color: CRIT }}>{formatCr(a.overdue_total)}</span>
                    <span className="block text-[10px] text-slate-400 tabular-nums">
                      P {formatCr(a.principal_overdue)} · I {formatCr(a.interest_overdue)}{a.other_overdue > 0 ? ` · Fees ${formatCr(a.other_overdue)}` : ""}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          ) : <div className="text-slate-400 text-sm py-8 text-center">No overdue accounts at this month-end.</div>}
        </Card>

        <Card title="AUM mix — Term Loans vs Invoice Discounting" note="Outstanding by product family · click to open the loan book"
          className="lg:col-span-5" testid="card-aum-mix">
          <div className="flex w-full h-9 rounded-sm overflow-hidden border border-slate-200 mb-4">
            <button className="ceo-seg h-full" style={{ width: `${(termVal / mixTotal) * 100}%`, background: INK }}
              title={`Term Loans: ${formatCr(termVal)}`} onClick={() => goLoans()} data-testid="aum-mix-term" />
            <button className="ceo-seg h-full" style={{ width: `${(idVal / mixTotal) * 100}%`, background: BRAND }}
              title={`Invoice Discounting: ${formatCr(idVal)}`} onClick={() => goLoans({ product: "Invoice Discounting" })} data-testid="aum-mix-id" />
          </div>
          <div className="grid grid-cols-2 gap-4 mb-5">
            <div>
              <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: INK }} />
                <span className="ceo-label !normal-case !tracking-normal !text-[12px]">Term Loans</span></div>
              <div className="ceo-fig text-[20px] text-[#111111] mt-1 tabular-nums">{formatCr(termVal)}</div>
              <div className="text-[11px] text-slate-400">{((termVal / mixTotal) * 100).toFixed(1)}% of AUM</div>
            </div>
            <div>
              <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: BRAND }} />
                <span className="ceo-label !normal-case !tracking-normal !text-[12px]">Invoice Discounting</span></div>
              <div className="ceo-fig text-[20px] text-[#111111] mt-1 tabular-nums">{formatCr(idVal)}</div>
              <div className="text-[11px] text-slate-400">{((idVal / mixTotal) * 100).toFixed(1)}% of AUM</div>
            </div>
          </div>
          <div className="pt-4 border-t border-slate-100 space-y-1.5">
            {byProduct.filter((p) => p.value > 0).map((p) => (
              <button key={p.name} onClick={() => goLoans({ product: p.name })}
                className="ceo-row w-full flex items-center justify-between rounded px-2 py-1 -mx-2 text-left" data-testid={`aum-mix-product-${p.name}`}>
                <span className="text-[12px] text-slate-600 truncate">{p.name}</span>
                <span className="text-[12px] font-semibold text-[#111111] tabular-nums shrink-0 ml-2">{formatCr(p.value)}</span>
              </button>
            ))}
          </div>
        </Card>
      </div>

      {/* DPD aging */}
      <Card title="Loan tracking — DPD aging" note="Outstanding by bucket · click a segment to open the filtered loan book"
        className="mb-8" testid="card-dpd">
        <div className="flex w-full h-9 rounded-sm overflow-hidden border border-slate-200" data-testid="dpd-bar">
          {dpd.map((d) => (
            <button key={d.name} className="ceo-seg h-full" data-testid={`dpd-seg-${d.name}`}
              onClick={() => goLoans({ bucket: d.name })}
              title={`${d.name}: ${formatCr(d.outstanding)} · ${round0(d.count)} accts`}
              style={{ width: `${(d.outstanding / dpdTotal) * 100}%`, background: BUCKET_COLOR[d.name] }} />
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-5">
          {dpd.map((d) => (
            <button key={d.name} onClick={() => goLoans({ bucket: d.name })} data-testid={`dpd-legend-${d.name}`}
              className="ceo-row text-left rounded p-2 -m-2">
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: BUCKET_COLOR[d.name] }} />
                <span className="text-[12px] text-slate-500">{d.name}</span>
              </div>
              <div className="ceo-fig text-[16px] text-[#111111] mt-1 tabular-nums">{formatCr(d.outstanding)}</div>
              <div className="text-[11px] text-slate-400">{round0(d.count)} accts · {((d.outstanding / dpdTotal) * 100).toFixed(1)}%</div>
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2.5 mt-5 pt-4 border-t border-slate-100">
          <span className="text-[11px] text-slate-400 self-center">Derived from the above bands:</span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-[#eab308]/10" data-testid="par30-chip">
            <span className="text-[12px] text-slate-500">PAR&gt;30</span>
            <span className="text-[13px] font-bold" style={{ color: "#a16207" }}>{formatCr(par30)}</span>
          </span>
          <button onClick={() => goLoans({ bucket: "91-365 DPD" })} data-testid="par90-chip"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-[#DC2626]/10 hover:bg-[#DC2626]/15 transition-colors">
            <span className="text-[12px] text-slate-500">PAR&gt;90</span>
            <span className="text-[13px] font-bold" style={{ color: CRIT }}>{formatCr(par90)}</span>
          </button>
        </div>
      </Card>

      {/* AUM trend + Concentration */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
        <Card title="AUM trend" note="Outstanding · trailing 12 month-ends" className="lg:col-span-7" testid="card-aum-trend">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={aumTrend} margin={{ left: -4, right: 10, top: 6 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={(v) => formatCr(v)} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={62} />
              <Tooltip formatter={(v) => formatCr(v)} contentStyle={tooltipStyle} />
              <Line type="monotone" dataKey="aum" stroke={BRAND} strokeWidth={2.5} dot={false} name="AUM" />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Concentration" note="Active book · share of AUM" className="lg:col-span-5"
          testid="card-concentration"
          right={
            <div className="flex items-center gap-1.5">
              <span className="ceo-label">Cap %</span>
              <input value={cap} onChange={(e) => onCap(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="set"
                data-testid="concentration-cap" inputMode="decimal"
                className="w-16 px-2 py-1 rounded-none border-0 border-b-2 border-slate-900 text-sm text-right focus:border-[#b3122a] outline-none bg-transparent" />
            </div>
          }>
          <div className="grid grid-cols-2 gap-4 mb-5">
            <MiniStat label="Largest single borrower" value={pct1(largestPct)} />
            <MiniStat label="Top-10 borrowers" value={pct1(top10pct)} />
          </div>
          {capNum != null ? (
            flagged.length ? (
              <div data-testid="concentration-flags">
                <div className="text-[12px] font-bold text-[#DC2626] mb-1.5">{flagged.length} borrower(s) above {pct1(capNum)} cap</div>
                <div className="space-y-1">
                  {flagged.map((b) => (
                    <button key={b.name} onClick={() => goLoans({ borrower: b.name })}
                      className="ceo-row w-full flex items-center justify-between text-left rounded px-2 py-1 -mx-2">
                      <span className="text-[13px] text-[#111111] truncate">{b.name}</span>
                      <span className="text-[13px] font-bold tabular-nums" style={{ color: CRIT }}>{((b.value / activeAum) * 100).toFixed(1)}%</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-[13px] text-slate-400" data-testid="concentration-flags">No borrower exceeds the {pct1(capNum)} cap.</div>
            )
          ) : (
            <div className="text-[12px] text-slate-400 flex items-center gap-1.5"><Info size={13} strokeWidth={1.5} /> Set a cap to flag over-concentrated borrowers.</div>
          )}
        </Card>
      </div>

      {/* Origination funnel + DSA snapshot */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Origination pipeline (LOS)"
          note={isLatest ? "Current applicant state" : "Current applicant state · not month-end snapshotted"}
          testid="card-funnel"
          right={pipe && (
            <div className="text-right">
              <div className="ceo-label">Approval rate</div>
              <div className="ceo-fig text-[18px] text-[#111111] tabular-nums">{pct1(pipe.approval_rate)}</div>
            </div>
          )}>
          <div className="space-y-4" data-testid="funnel">
            {funnel.map((f) => (
              <div key={f.label}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[13px] text-slate-600">{f.label}</span>
                  <span className="text-[13px] font-bold text-[#111111] tabular-nums">{round0(f.count)}</span>
                </div>
                <div className="h-2.5 rounded-sm bg-slate-100 overflow-hidden">
                  <div className="h-full rounded-sm" style={{ width: `${(f.count / fnMax) * 100}%`, background: BRAND }} />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
            <span className="text-[12px] text-slate-500">Avg ticket (approved)</span>
            <span className="ceo-fig text-[15px] text-[#111111] tabular-nums">{approved ? formatCr(approved.avg_ticket) : "—"}</span>
          </div>
        </Card>

        <Card title="DSA network"
          note={isLatest ? "Current network state" : "Current network state · not month-end snapshotted"}
          testid="card-dsa">
          {dsa ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { l: "Total DSAs", v: round0(dsa.total) },
                  { l: "Relevant", v: round0(dsa.relevant), s: pct1(dsa.relevant_pct) },
                  { l: "Approved", v: round0(dsa.approved) },
                  { l: "E-signed", v: round0(dsa.esigned) },
                ].map((x) => (
                  <div key={x.l} className="border border-slate-100 rounded p-3 bg-[#fafbfc]">
                    <div className="text-[11px] text-slate-500">{x.l}</div>
                    <div className="ceo-fig text-[19px] text-[#111111] mt-1 tabular-nums">{x.v}</div>
                    {x.s && <div className="text-[11px] text-slate-400">{x.s}</div>}
                  </div>
                ))}
              </div>
              <div className="mt-4 text-[12px] text-slate-400 flex items-start gap-1.5">
                <Info size={13} strokeWidth={1.5} className="mt-0.5 shrink-0" />
                Per-DSA productivity is not shown: there is no application→DSA attribution in this system.
              </div>
            </>
          ) : <div className="text-slate-400 text-sm">Loading…</div>}
        </Card>
      </div>
    </div>
  );
}
